import { useState, useEffect, useCallback } from "react";
import {
  Paperclip,
  X,
  Download,
  Loader2,
  FileText,
  ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import heic2any from "heic2any";
import imageCompression from "browser-image-compression";
import * as db from "@/lib/db";

const MAX_UPLOAD_MB = 15;

const IMAGE_COMPRESS_OPTS = {
  maxSizeMB: 1.5,
  maxWidthOrHeight: 2048,
  useWebWorker: true,
  fileType: "image/jpeg",
  initialQuality: 0.82,
};

const isHeic = (file) => {
  const t = (file.type || "").toLowerCase();
  const n = (file.name || "").toLowerCase();

  return (
    t === "image/heic" ||
    t === "image/heif" ||
    n.endsWith(".heic") ||
    n.endsWith(".heif")
  );
};

const isPdf = (file) =>
  (file.type || "").toLowerCase() === "application/pdf" ||
  (file.name || "").toLowerCase().endsWith(".pdf");

const isImage = (file) =>
  (file.type || "").toLowerCase().startsWith("image/") ||
  /\.(jpe?g|png|heic|heif)$/i.test(file.name || "");

/**
 * Shared receipt preparation helper.
 *
 * Used by:
 * - ReceiptUploader for existing records
 * - TransactionForm when creating a new transaction with receipt attached
 */
export const prepareReceiptFile = async (file) => {
  if (!file) {
    throw new Error("No receipt file selected.");
  }

  if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
    throw new Error(
      `File too large. Maximum ${MAX_UPLOAD_MB} MB.`
    );
  }

  let processed = file;
  let ext = (file.name || "")
    .split(".")
    .pop()
    .toLowerCase();

  let mime =
    file.type || "application/octet-stream";

  if (isPdf(file)) {
    if (file.size > 8 * 1024 * 1024) {
      toast.warning(
        "Large PDF — storage will use up space faster."
      );
    }

    ext = "pdf";
    mime = "application/pdf";
  } else if (isHeic(file)) {
    const result = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.85,
    });

    // heic2any can theoretically return an array.
    const blob = Array.isArray(result)
      ? result[0]
      : result;

    const converted = new File(
      [blob],
      file.name.replace(
        /\.(heic|heif)$/i,
        ".jpg"
      ),
      {
        type: "image/jpeg",
      }
    );

    processed = await imageCompression(
      converted,
      IMAGE_COMPRESS_OPTS
    );

    ext = "jpg";
    mime = "image/jpeg";
  } else if (isImage(file)) {
    processed = await imageCompression(
      file,
      IMAGE_COMPRESS_OPTS
    );

    ext = "jpg";
    mime = "image/jpeg";
  } else {
    throw new Error(
      "Unsupported file format. Please upload JPG, PNG, HEIC, HEIF or PDF."
    );
  }

  return {
    file: processed,
    ext,
    mime,
  };
};

/**
 * ReceiptUploader
 *
 * Props:
 * entityType:
 *   "transaction" | "asset" | "tax_adjustment"
 *
 * entityId:
 *   UUID of an existing record.
 *
 * pendingFiles:
 *   For a new transaction that does not have an ID yet.
 *   Can contain one or multiple files.
 *
 * onPendingFilesChange:
 *   Allows TransactionForm to hold selected receipts temporarily
 *   until the transaction is created.
 */
const ReceiptUploader = ({
  entityType,
  entityId,
  compact = false,
  pendingFiles = [],
  onPendingFilesChange = null,
}) => {
  const [attachments, setAttachments] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [uploading, setUploading] =
    useState(false);

  const [openingId, setOpeningId] =
    useState(null);

  const refresh = useCallback(async () => {
    if (!entityId) {
      setAttachments([]);
      return;
    }

    setLoading(true);

    try {
      const rows =
        await db.listAttachments(
          entityType,
          entityId
        );

      setAttachments(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleFile = async (file) => {
    if (!entityId) {
      toast.error(
        "Please save the record first, then attach receipts."
      );
      return;
    }

    setUploading(true);

    const tId = toast.loading(
      isHeic(file)
        ? "Converting HEIC…"
        : "Uploading receipt…"
    );

    try {
      const prepared =
        await prepareReceiptFile(file);

      await db.uploadAttachment({
        entityType,
        entityId,
        file: prepared.file,
        ext: prepared.ext,
        mime: prepared.mime,
      });

      toast.success("Receipt attached", {
        id: tId,
      });

      refresh();
    } catch (e) {
      console.error(e);

      toast.error(
        e.message ||
          "Could not process this receipt image. Please try a different file or format.",
        {
          id: tId,
        }
      );
    } finally {
      setUploading(false);
    }
  };

  const onInput = (e) => {
    const files = Array.from(
      e.target.files || []
    );

    if (files.length === 0) {
      e.target.value = "";
      return;
    }

    /*
     * NEW TRANSACTION
     *
     * Keep one or multiple selected files locally
     * until Save Transaction is clicked.
     *
     * If files were already selected, newly selected
     * files are ADDED rather than replacing them.
     */
    if (
      !entityId &&
      onPendingFilesChange
    ) {
      const currentFiles =
        Array.isArray(pendingFiles)
          ? pendingFiles
          : [];

      onPendingFilesChange([
        ...currentFiles,
        ...files,
      ]);

      e.target.value = "";
      return;
    }

    /*
     * EXISTING RECORD
     *
     * Upload immediately as before.
     */
    files.forEach(handleFile);

    e.target.value = "";
  };

  const openAttachment = async (a) => {
    setOpeningId(a.id);

    try {
      const url =
        await db.getAttachmentSignedUrl(
          a.filePath,
          3600
        );

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (e) {
      console.error(e);
      toast.error(
        "Could not open receipt"
      );
    } finally {
      setOpeningId(null);
    }
  };

  const removeAttachment = async (a) => {
    if (
      !window.confirm(
        "Remove this receipt from the record? The original file will also be deleted from storage."
      )
    ) {
      return;
    }

    try {
      await db.deleteAttachment(a);

      toast.success(
        "Receipt removed"
      );

      refresh();
    } catch (e) {
      console.error(e);

      toast.error(
        "Could not remove receipt"
      );
    }
  };

  const removePendingFile = (index) => {
    if (!onPendingFilesChange) {
      return;
    }

    const nextFiles =
      pendingFiles.filter(
        (_, i) => i !== index
      );

    onPendingFilesChange(
      nextFiles
    );
  };

  const iconFor = (a) =>
    (a.fileType || "").startsWith(
      "image/"
    ) ? (
      <ImageIcon className="w-4 h-4 text-slate-500" />
    ) : (
      <FileText className="w-4 h-4 text-slate-500" />
    );

  const pendingCount =
    Array.isArray(pendingFiles)
      ? pendingFiles.length
      : 0;

  return (
    <div
      className="space-y-2"
      data-testid="receipt-uploader"
    >
      {!compact && (
        <p className="text-[11px] text-slate-500">
          Accepted: JPG, PNG, HEIC, HEIF,
          PDF · Max {MAX_UPLOAD_MB} MB ·
          iPhone HEIC is auto-converted to
          JPEG.
        </p>
      )}

      <label
        className={`flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-[#193f7d] hover:bg-slate-100 text-sm font-semibold text-slate-600 hover:text-[#193f7d] cursor-pointer transition-colors ${
          uploading
            ? "opacity-60 pointer-events-none"
            : ""
        }`}
        data-testid="receipt-upload-label"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Paperclip className="w-4 h-4" />
        )}

        {uploading
          ? "Uploading…"
          : entityId
          ? "Attach receipt"
          : pendingCount > 0
          ? `${pendingCount} file${
              pendingCount === 1
                ? ""
                : "s"
            } selected`
          : "Select receipt"}

        <input
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf,.heic,.heif"
          onChange={onInput}
          className="hidden"
          disabled={uploading}
          data-testid="receipt-file-input"
        />
      </label>

      {/* Pending files before saving a new Transaction */}
      {!entityId &&
        pendingCount > 0 && (
          <div className="space-y-2">
            {pendingFiles.map(
              (file, index) => (
                <div
                  key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl"
                >
                  <Paperclip className="w-4 h-4 text-emerald-700 flex-shrink-0" />

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-emerald-800 truncate">
                      {file.name}
                    </p>

                    <p className="text-[10px] text-emerald-600">
                      Ready to upload when you
                      save the transaction
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      removePendingFile(
                        index
                      )
                    }
                    className="p-1.5 rounded-full hover:bg-emerald-100 text-emerald-700"
                    aria-label="Remove selected receipt"
                    title="Remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )
            )}
          </div>
        )}

      {loading ? (
        <p className="text-xs text-slate-500">
          Loading attachments…
        </p>
      ) : attachments.length > 0 ? (
        <ul
          className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-200"
          data-testid="receipt-list"
        >
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 px-3 py-2"
              data-testid={`receipt-item-${a.id}`}
            >
              {iconFor(a)}

              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-700 truncate">
                  {a.fileName}
                </p>

                <p className="text-[10px] text-slate-400">
                  {a.fileType}
                  {" · "}
                  {a.fileSize
                    ? `${(
                        a.fileSize /
                        1024
                      ).toFixed(0)} KB`
                    : ""}
                  {" · "}
                  {a.uploadedAt?.slice(
                    0,
                    10
                  )}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  openAttachment(a)
                }
                disabled={
                  openingId === a.id
                }
                className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-[#193f7d] disabled:opacity-60"
                data-testid={`receipt-open-${a.id}`}
                aria-label="Open"
                title="Open receipt"
              >
                {openingId === a.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
              </button>

              <button
                type="button"
                onClick={() =>
                  removeAttachment(a)
                }
                className="p-1.5 rounded-full hover:bg-red-50 text-slate-500 hover:text-red-700"
                data-testid={`receipt-remove-${a.id}`}
                aria-label="Remove"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default ReceiptUploader;
